import { motion } from "motion/react";
import { Input } from "../../@shadcn/components/ui/input";
import {
  GIFT_CARD_INPUT_ERROR_CLASS,
  GIFT_CARD_INPUT_ERROR_STYLE,
  GIFT_CARD_INPUT_FOCUS_CLASS,
  getGiftCardInputFocusStyle,
} from "../commons/constants";
import { useAnimatedHeight } from "../commons/useAnimatedHeight";
import { GiftCardInputFieldType, GiftCardType } from "../types";
import GiftCardGIFTRRedeemInput from "./GiftCardGIFTRRedeemInput";
import GiftCardRedeemAmountSlider from "./GiftCardRedeemAmountSlider";
import GiftCardZeroBalanceStrip from "./GiftCardZeroBalanceStrip";

/** Shared by the code fields and the read-only full-amount field, which are the same control. */
const FIELD_STYLE = {
  height: 48,
  borderRadius: 10,
  backgroundColor: "#f4f4f5",
  paddingLeft: 14,
  paddingRight: 14,
  fontSize: 16,
  lineHeight: "24px",
  letterSpacing: "-0.16px",
  fontWeight: 400,
} as const;

const getPlaceholder = (field: GiftCardInputFieldType) =>
  field.validation.input_type === "NUMERIC" && field.validation.length
    ? `${field.validation.length} digit code`
    : field.title;

export interface GiftCardInputRegionProps {
  card: GiftCardType;
  accentColor: string;
  onInputChange: (title: string, value: string) => void;
  onRedeemAmountChange: (value: number) => void;
  error?: Error | null;
  playIntro?: boolean;
  focusRedeemAmount?: boolean;
  /** GYFTR at a zero balance — shown in this same region so it shares one height animation with
   * the rest of the card's inputs instead of resizing on its own, a beat apart. */
  showZeroBalanceNotice?: boolean;
  zeroBalanceMessage?: string;
  zeroBalanceSubtext?: string;
  /** True inside the merchant dashboard's customise-UI preview — see `GiftCardRedeemAmountSlider`. */
  dashboardOnly?: boolean;
}

/**
 * Every input the entry screen can show for a card, and the height between them.
 *
 * The screen shows at most two things stacked: the code fields, and a control for the amount.
 * Height is the only thing that animates — the inputs themselves are plain conditionals, with no
 * enter or exit of their own, so validating a card reveals the amount by opening the region rather
 * than by fading a second control in over it. The zero-balance notice lives here for the same
 * reason: one `useAnimatedHeight` instance covering everything the card can show means one resize
 * per change instead of this region and a separate notice each easing on their own.
 */
const GiftCardInputRegion = ({
  card,
  accentColor,
  onInputChange,
  onRedeemAmountChange,
  error = null,
  playIntro,
  focusRedeemAmount,
  showZeroBalanceNotice = false,
  zeroBalanceMessage,
  zeroBalanceSubtext,
  dashboardOnly = false,
}: GiftCardInputRegionProps) => {
  const { contentRef, height } = useAnimatedHeight();

  const isGyftr = card.provider === "GYFTR";
  const inputs = card.input_config.inputs;
  const isValidating = card.actions.VALIDATING.loading;

  // GYFTR takes its voucher code on its own screen, so no inline code fields here.
  const showCodeSlot = inputs.length > 0 && !isGyftr;
  // A GYFTR wallet is looked up the moment it is selected, so it has an amount from the start.
  // Every other card has nothing to put an amount against until its code comes back validated.
  const showAmountSlot = isGyftr
    ? inputs.length > 0
    : card.codeValidated && card.amount_strategy === "CUSTOM";

  // FULL_AMOUNT spends the whole balance, so there is nothing for the shopper to pick and the
  // slider is replaced by a read-only field stating the amount. Only a wallet reaches this: any
  // other card is given an amount slot only when its strategy is CUSTOM.
  const isFullAmount = isGyftr && card.amount_strategy === "FULL_AMOUNT";

  const errorLine = Boolean(error) && (
    <p className="ui-px-1 ui-text-text-xs-medium ui-text-red-600">
      {error?.message}
    </p>
  );

  const amountControl = isFullAmount ? (
    // Nothing to pick, so the read-only field waits on the balance the same way the slider does.
    isValidating ? (
      <GiftCardGIFTRRedeemInput accentColor={accentColor} />
    ) : (
      <>
        <Input
          disabled
          readOnly
          aria-label="Redeem amount"
          value={card.redeemBalance}
          className="ui-border-0 !ui-py-0 !ui-opacity-100 !ui-shadow-none"
          style={{ ...FIELD_STYLE, color: "rgba(0, 0, 0, 0.8)" }}
        />
        <p className="ui-px-1 ui-text-text-xs-medium ui-text-zinc-500">
          Gift card will redeem full amount
        </p>
      </>
    )
  ) : isGyftr ? (
    <GiftCardRedeemAmountSlider
      value={card.redeemValue}
      max={card.redeemBalance}
      onChange={onRedeemAmountChange}
      accentColor={accentColor}
      focusInput={false}
      loading={isValidating}
      hideOnEmptyBalance
      playIntro={playIntro}
      dashboardOnly={dashboardOnly}
    />
  ) : (
    <GiftCardRedeemAmountSlider
      value={card.redeemValue}
      max={card.redeemBalance}
      onChange={onRedeemAmountChange}
      accentColor={accentColor}
      playIntro={playIntro}
      focusInput={false}
      dashboardOnly={dashboardOnly}
    />
  );

  return (
    <motion.div
      className="ui-flex ui-flex-col ui-justify-end ui-overflow-hidden mt-2"
      style={{ height,marginBottom: 16 }}
    >
      {/* `shrink-0`: a column flex item is shrinkable by default, and this one is deliberately
          taller than its parent for most of the animation. Left to shrink it would be squeezed to
          the clipped height, and the observer below would measure that instead of the height it is
          easing towards — reading its own animation back as the next target. */}
      <div ref={contentRef} className="ui-shrink-0">
        <div className="ui-px-4">
          <div className="ui-flex ui-flex-col ui-gap-1">
            {showCodeSlot && (
              <div className="ui-flex ui-items-start ui-gap-2">
                {inputs.map((field, index) => {
                  // Card number takes the remaining space; a trailing short field (e.g. PIN) gets
                  // a fixed width.
                  const isFixedWidth =
                    inputs.length > 1 && index === inputs.length - 1;
                  return (
                    <Input
                      key={field.title}
                      placeholder={getPlaceholder(field)}
                      maxLength={field.validation.length}
                      inputMode={
                        field.validation.input_type === "NUMERIC"
                          ? "numeric"
                          : "text"
                      }
                      value={card.values[field.title] ?? ""}
                      onChange={(e) => onInputChange(field.title, e.target.value)}
                      error={Boolean(error)}
                      className={`ui-border-0 placeholder:ui-text-[#9f9fa9] !ui-py-0 !ui-shadow-none ${GIFT_CARD_INPUT_FOCUS_CLASS} ${GIFT_CARD_INPUT_ERROR_CLASS}`}
                      style={{
                        ...getGiftCardInputFocusStyle(accentColor),
                        ...GIFT_CARD_INPUT_ERROR_STYLE,
                        ...FIELD_STYLE,
                        ...(isFixedWidth
                          ? { flex: "0 0 114px", width: 114 }
                          : { flex: "1 0 0", minWidth: 0 }),
                      }}
                    />
                  );
                })}
              </div>
            )}

            {/* The error belongs under the field it describes, which is the code row whenever there
                is one. A wallet has none, so its lookup and apply failures land under the amount. */}
            {showCodeSlot && errorLine}
          </div>

          {showAmountSlot && (
            <div
              className={`ui-flex ui-w-full ui-flex-col ui-gap-1${showCodeSlot ? " ui-pt-3" : ""}`}
            >
              {amountControl}
              {!showCodeSlot && errorLine}
            </div>
          )}
        </div>

        {/* Unpadded, unlike the block above: `GiftCardZeroBalanceStrip` carries its own
            edge-to-edge `px-5`, the same as when it lived directly in the screen's footer. */}

        {showZeroBalanceNotice && zeroBalanceMessage && (
          <div className="ui-pt-3">
            <GiftCardZeroBalanceStrip
              message={zeroBalanceMessage}
              subtext={zeroBalanceSubtext}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default GiftCardInputRegion;
